// iter119 — BFR Ops Calendar Dashboard (aggregate across ALL properties).
// Design mirrors the owner-facing CalendarScreen for UX consistency, but:
//   • counts aggregate across every property (or filter to one)
//   • per-day cells render a per-property breakdown tooltip
//   • summary cards show Today's Check-ins/Check-outs/Bookings/No-Shows/Walk-ins
//   • clicking a date lazy-loads the full booking list (no upfront payload)
// Read-only for the superhost_admin role. Zero mutations.
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { adminAPI } from '../../services/api';
import { formatGuestBreakdown, rawGuestValue } from '../../utils/formatGuests';
import OtaLogo from '../../components/OtaLogo';
import SendEmailModal from '../../components/SendEmailModal';
import {
  format, startOfMonth, endOfMonth, eachDayOfInterval,
  isSameDay, addMonths, subMonths,
} from 'date-fns';
import {
  ShieldCheck, LogOut, ChevronLeft, ChevronRight, Calendar as CalendarIcon,
  ArrowDownToLine, ArrowUpFromLine, Loader2, X, Search, Building2,
  User, Phone, Home, Globe, RefreshCw, LogIn, UserX, ChevronDown, ArrowLeft,
  Ban, IndianRupee, Mail, MapPin,
} from 'lucide-react';

const FILTER_OPTIONS = [
  { value: 'booking',  label: 'Booking Date', icon: CalendarIcon },
  { value: 'checkin',  label: 'Check In',     icon: ArrowDownToLine },
  { value: 'checkout', label: 'Check Out',    icon: ArrowUpFromLine },
];

const REFRESH_MS = 60_000; // live refresh cadence for counts + summary

const AdminCalendarScreen = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const [currentDate, setCurrentDate] = useState(new Date());
  const [filterType, setFilterType] = useState('checkin');
  const [selectedDate, setSelectedDate] = useState(null);
  const [propertyFilter, setPropertyFilter] = useState('');       // '' = all
  const [propertyOptions, setPropertyOptions] = useState([]);
  const [propertyDropdownOpen, setPropertyDropdownOpen] = useState(false);

  const [countsData, setCountsData] = useState({
    counts: { checkin: {}, checkout: {}, booking: {} },
    breakdown: { checkin: {}, checkout: {}, booking: {} },
    month_total: { checkin: 0, checkout: 0, booking: 0 },
  });
  const [summary, setSummary] = useState({
    checkins_today: 0, checkouts_today: 0, bookings_today: 0,
    noshows_today: 0, walkins_today: 0, rejected_today: 0,
  });
  const [countsLoading, setCountsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Date-detail popup state
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupDate, setPopupDate] = useState(null);
  const [popupBookings, setPopupBookings] = useState([]);
  const [popupLoading, setPopupLoading] = useState(false);
  const [popupSearch, setPopupSearch] = useState('');
  const [popupStatusFilter, setPopupStatusFilter] = useState('all');

  const refreshTimerRef = useRef(null);

  const monthStart = useMemo(() => startOfMonth(currentDate), [currentDate]);
  const monthEnd   = useMemo(() => endOfMonth(currentDate),   [currentDate]);

  const loadPropertyOptions = useCallback(async () => {
    try {
      const res = await adminAPI.calendarProperties();
      setPropertyOptions(res?.STATUS === 1 ? (res.RESULT || []) : []);
    } catch {
      setPropertyOptions([]);
    }
  }, []);

  const loadCountsAndSummary = useCallback(async () => {
    setCountsLoading(true);
    try {
      const start = format(monthStart, 'yyyy-MM-dd');
      const end   = format(monthEnd,   'yyyy-MM-dd');
      const pid = propertyFilter || null;
      const [countsRes, summaryRes] = await Promise.all([
        adminAPI.calendarCounts(start, end, pid),
        adminAPI.calendarSummary(pid),
      ]);
      if (countsRes?.STATUS === 1) {
        setCountsData({
          counts:      countsRes.RESULT?.counts      || { checkin: {}, checkout: {}, booking: {} },
          breakdown:   countsRes.RESULT?.breakdown   || { checkin: {}, checkout: {}, booking: {} },
          month_total: countsRes.RESULT?.month_total || { checkin: 0, checkout: 0, booking: 0 },
        });
      }
      if (summaryRes?.STATUS === 1) setSummary(summaryRes.RESULT || {});
    } finally {
      setCountsLoading(false);
    }
  }, [monthStart, monthEnd, propertyFilter]);

  useEffect(() => { loadPropertyOptions(); }, [loadPropertyOptions]);
  useEffect(() => { loadCountsAndSummary(); }, [loadCountsAndSummary]);

  // Live refresh — counts + summary auto-poll while the tab is active.
  useEffect(() => {
    if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    refreshTimerRef.current = setInterval(() => {
      if (document.visibilityState === 'visible') loadCountsAndSummary();
    }, REFRESH_MS);
    return () => clearInterval(refreshTimerRef.current);
  }, [loadCountsAndSummary]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadCountsAndSummary();
    setRefreshing(false);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin-login', { replace: true });
  };

  // Calendar grid days including leading blanks
  const calendarDays = useMemo(() => {
    const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
    const padding = Array(monthStart.getDay()).fill(null);
    return [...padding, ...days];
  }, [monthStart, monthEnd]);

  const getDayCount = useCallback((date) => {
    if (!date) return 0;
    const key = format(date, 'yyyy-MM-dd');
    return countsData.counts?.[filterType]?.[key] || 0;
  }, [countsData, filterType]);

  const getDayBreakdown = useCallback((date) => {
    if (!date) return [];
    const key = format(date, 'yyyy-MM-dd');
    return countsData.breakdown?.[filterType]?.[key] || [];
  }, [countsData, filterType]);

  const monthTotalForMode = countsData.month_total?.[filterType] || 0;

  const openDatePopup = useCallback(async (date) => {
    if (!date) return;
    const dateKey = format(date, 'yyyy-MM-dd');
    setPopupDate(dateKey);
    setPopupOpen(true);
    setPopupSearch('');
    setPopupStatusFilter('all');
    setPopupLoading(true);
    setSelectedDate(date);
    try {
      const res = await adminAPI.calendarBookingsForDate(
        dateKey, filterType, propertyFilter || null, '',
      );
      setPopupBookings(res?.STATUS === 1 ? (res.RESULT?.bookings || []) : []);
    } catch {
      setPopupBookings([]);
    } finally {
      setPopupLoading(false);
    }
  }, [filterType, propertyFilter]);

  // iter154 — Live-refresh the OPEN popup every 30 s so a Hotel Owner
  // check-in (writes `checkin_decision='confirmed'`) propagates to the
  // BFR screen without a manual close/reopen. Runs only when the popup
  // is open AND the tab is visible; silent (no spinner) to avoid flicker.
  useEffect(() => {
    if (!popupOpen || !popupDate) return undefined;
    const tick = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const res = await adminAPI.calendarBookingsForDate(
          popupDate, filterType, propertyFilter || null, '',
        );
        if (res?.STATUS === 1) {
          setPopupBookings(res.RESULT?.bookings || []);
        }
      } catch { /* transient network — retry next tick */ }
    };
    const id = setInterval(tick, 30_000);
    // Also refresh immediately when the tab regains focus.
    const onVis = () => { if (document.visibilityState === 'visible') tick(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [popupOpen, popupDate, filterType, propertyFilter]);

  const closePopup = () => {
    setPopupOpen(false);
    setPopupBookings([]);
    setPopupDate(null);
  };

  const handleFilterChange = (value) => {
    setFilterType(value);
    setSelectedDate(null);
  };

  const goToToday = () => {
    const today = new Date();
    setCurrentDate(today);
    setSelectedDate(today);
  };

  const selectedFilter = FILTER_OPTIONS.find(f => f.value === filterType);

  // Client-side filtering inside popup (search + status pill).
  const filteredPopupBookings = useMemo(() => {
    let list = popupBookings;
    if (popupStatusFilter !== 'all') {
      list = list.filter((b) => {
        const bs = (b.booking_status || '').toString().toLowerCase();
        if (popupStatusFilter === 'checked_in')  return b.checkin_decision === 'confirmed' || bs === 'checked_in';
        if (popupStatusFilter === 'no_show')     return b.checkin_decision === 'no_show'   || bs === 'no_show';
        if (popupStatusFilter === 'walkin')      return !!b.is_walkin;
        if (popupStatusFilter === 'confirmed')   return !b.checkin_decision && bs !== 'cancelled' && bs !== 'checked_out';
        return true;
      });
    }
    if (popupSearch.trim()) {
      const term = popupSearch.trim().toLowerCase();
      list = list.filter((b) => {
        const hay = [
          b.booking_id, b.guest_name, b.guest_phone, b.ota_source,
          b.property_name, b.property_owner, b.room_type,
        ].map((x) => String(x || '').toLowerCase()).join(' ');
        return hay.includes(term);
      });
    }
    return list;
  }, [popupBookings, popupSearch, popupStatusFilter]);

  const selectedPropertyName = propertyOptions.find(p => p.property_id === propertyFilter)?.property_name;

  return (
    <div className="min-h-screen bg-white pb-28" data-testid="admin-calendar-screen">
      {/* Header — navy brand strip. iter159 redesign. */}
      <div className="bg-[#1B2A7B] border-b border-[#152163] sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/admin')}
              className="p-1.5 rounded-lg hover:bg-white/10 text-white/80 hover:text-white"
              data-testid="admin-calendar-back-btn"
              aria-label="Back to admin dashboard"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="w-9 h-9 rounded-lg bg-white/15 border border-white/25 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-white font-bold text-base leading-tight">Superhost Calendar</h1>
              <p className="text-[11px] text-white/70">{user?.name} · Read-only · All Properties</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-1.5 text-white/80 hover:text-white text-sm px-3 py-1.5 rounded-lg hover:bg-white/10"
            data-testid="admin-calendar-logout-btn"
          >
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-5 space-y-5">
        {/* Today Summary Cards — iter120: added Rejected as the 6th metric. */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="admin-calendar-summary">
          <SummaryCard
            testid="summary-checkins"
            label="Check-ins"
            value={summary.checkins_today}
            tone="emerald"
            icon={<ArrowDownToLine className="w-4 h-4" />}
          />
          <SummaryCard
            testid="summary-checkouts"
            label="Check-outs"
            value={summary.checkouts_today}
            tone="rose"
            icon={<ArrowUpFromLine className="w-4 h-4" />}
          />
          <SummaryCard
            testid="summary-total-bookings"
            label="Total Bookings"
            value={summary.bookings_today}
            tone="amber"
            icon={<CalendarIcon className="w-4 h-4" />}
          />
          <SummaryCard
            testid="summary-no-shows"
            label="No Shows"
            value={summary.noshows_today}
            tone="slate"
            icon={<UserX className="w-4 h-4" />}
          />
          <SummaryCard
            testid="summary-walkins"
            label="Walk-ins"
            value={summary.walkins_today}
            tone="sky"
            icon={<LogIn className="w-4 h-4" />}
          />
          <SummaryCard
            testid="summary-rejected"
            label="Rejected"
            value={summary.rejected_today}
            tone="red"
            icon={<Ban className="w-4 h-4" />}
          />
        </div>

        {/* Controls row */}
        <div className="flex flex-wrap items-center gap-2 justify-between">
          {/* Property Filter */}
          <div className="relative" data-testid="admin-property-filter-wrap">
            <button
              onClick={() => setPropertyDropdownOpen(v => !v)}
              className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-lg text-slate-800 text-sm hover:border-[#1B2A7B]/50"
              data-testid="admin-property-filter-btn"
            >
              <Building2 className="w-4 h-4 text-[#1B2A7B]" />
              <span className="truncate max-w-[220px]">
                {propertyFilter ? (selectedPropertyName || 'One property') : 'All Properties'}
              </span>
              <ChevronDown className="w-4 h-4 text-slate-500" />
            </button>
            {propertyDropdownOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setPropertyDropdownOpen(false)} />
                <div className="absolute left-0 top-full mt-1 z-20 w-80 max-h-72 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-2xl">
                  <button
                    onClick={() => { setPropertyFilter(''); setPropertyDropdownOpen(false); }}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-100 ${!propertyFilter ? 'bg-slate-100 text-[#1B2A7B]' : 'text-slate-700'}`}
                    data-testid="admin-property-filter-option-all"
                  >
                    All Properties
                  </button>
                  {propertyOptions.map((p) => (
                    <button
                      key={p.property_id}
                      onClick={() => { setPropertyFilter(p.property_id); setPropertyDropdownOpen(false); }}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-100 flex items-center justify-between ${propertyFilter === p.property_id ? 'bg-slate-100 text-[#1B2A7B]' : 'text-slate-700'}`}
                      data-testid={`admin-property-filter-option-${p.property_id}`}
                    >
                      <span className="truncate">{p.property_name}</span>
                      <span className="text-[11px] text-slate-500 ml-2">{p.booking_count}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Filter buttons */}
          <div className="flex gap-1.5 flex-wrap" data-testid="admin-filter-buttons">
            {FILTER_OPTIONS.map((option) => {
              const Icon = option.icon;
              const active = filterType === option.value;
              return (
                <button
                  key={option.value}
                  onClick={() => handleFilterChange(option.value)}
                  className={`
                    flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all border
                    ${active
                      ? 'bg-[#1B2A7B] text-white border-[#1B2A7B] shadow'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-[#1B2A7B]/40'}
                  `}
                  data-testid={`admin-filter-btn-${option.value}`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Month nav + count */}
        <div className="bg-white border border-slate-200 rounded-xl px-3 py-3 flex items-center justify-between">
          <button
            onClick={() => setCurrentDate(subMonths(currentDate, 1))}
            className="p-2 rounded-lg bg-slate-100 text-slate-800 hover:bg-slate-200"
            data-testid="admin-prev-month"
            aria-label="Previous month"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="text-center">
            <h2 className="text-slate-900 font-semibold text-base" data-testid="admin-current-month">
              {format(currentDate, 'MMMM yyyy')}
            </h2>
            <p className="text-[11px] uppercase tracking-wider text-[#1B2A7B]/80" data-testid="admin-month-total">
              {monthTotalForMode} {selectedFilter?.label.toLowerCase()}s this month
            </p>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={goToToday}
              className="px-3 py-1.5 rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200 text-xs font-medium"
              data-testid="admin-today-btn"
            >
              Today
            </button>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="p-2 rounded-lg bg-slate-100 text-slate-800 hover:bg-slate-200 disabled:opacity-50"
              data-testid="admin-refresh-btn"
              aria-label="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={() => setCurrentDate(addMonths(currentDate, 1))}
              className="p-2 rounded-lg bg-slate-100 text-slate-800 hover:bg-slate-200"
              data-testid="admin-next-month"
              aria-label="Next month"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Calendar grid */}
        <div className="bg-white border border-slate-200 rounded-xl p-3">
          <div className="grid grid-cols-7 gap-1 mb-2">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
              <div key={d} className="text-center text-[10px] uppercase tracking-wider text-slate-500 py-1">
                {d}
              </div>
            ))}
          </div>
          {countsLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 text-[#1B2A7B] animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-7 gap-1" data-testid="admin-calendar-grid">
              {calendarDays.map((day, idx) => {
                if (!day) return <div key={idx} className="aspect-square" />;
                const count = getDayCount(day);
                const breakdown = getDayBreakdown(day);
                const isToday = isSameDay(day, new Date());
                const isSelected = selectedDate && isSameDay(day, selectedDate);
                const hasCount = count > 0;
                return (
                  <button
                    key={idx}
                    onClick={() => openDatePopup(day)}
                    className={`
                      relative aspect-square rounded-lg p-1.5 text-left flex flex-col justify-between transition-all border
                      ${isSelected
                        ? 'bg-[#1B2A7B]/20 border-[#1B2A7B] ring-1 ring-[#1B2A7B]/40'
                        : hasCount
                          ? 'bg-slate-100 border-slate-200 hover:border-[#1B2A7B]/60'
                          : 'bg-white border-slate-200 hover:border-slate-200'}
                      ${isToday && !isSelected ? 'ring-1 ring-[#1B2A7B]/30' : ''}
                    `}
                    data-testid={`admin-calendar-day-${format(day, 'yyyy-MM-dd')}`}
                    title={
                      breakdown.length
                        ? `${format(day, 'MMM d')} · ${selectedFilter?.label}: ${count}\n` +
                          breakdown.slice(0, 6).map(b => `${b.property_name} — ${b.count}`).join('\n') +
                          (breakdown.length > 6 ? `\n… and ${breakdown.length - 6} more` : '')
                        : format(day, 'MMM d')
                    }
                  >
                    <span className={`text-xs font-medium ${isToday ? 'text-[#1B2A7B]' : hasCount ? 'text-slate-900' : 'text-slate-500'}`}>
                      {format(day, 'd')}
                    </span>
                    {hasCount && (
                      <div className="flex flex-col items-end">
                        <span className="text-lg font-bold text-[#1B2A7B] leading-none" data-testid={`admin-day-count-${format(day, 'yyyy-MM-dd')}`}>
                          {count}
                        </span>
                        {/* iter141 — Removed per-day "N props." property-count
                            text per product ask (was breaking the cell layout
                            on small screens). Full property breakdown is still
                            available via the cell's `title` tooltip and the
                            date-detail popup opened via `openDatePopup(day)`. */}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <p className="text-[11px] text-slate-500 text-center">
          Tap a date to view every booking on that day. Counts refresh automatically every minute.
        </p>
      </div>

      {/* Date-details Popup */}
      {popupOpen && (
        <DateDetailsPopup
          date={popupDate}
          mode={filterType}
          bookings={filteredPopupBookings}
          rawTotal={popupBookings.length}
          loading={popupLoading}
          search={popupSearch}
          onSearchChange={setPopupSearch}
          statusFilter={popupStatusFilter}
          onStatusFilterChange={setPopupStatusFilter}
          onClose={closePopup}
          propertyName={selectedPropertyName || 'All Properties'}
        />
      )}
    </div>
  );
};

const SummaryCard = ({ label, value, tone, icon, testid }) => {
  const toneMap = {
    emerald: 'from-green-100 to-green-50 border-[#1B2A7B]/30 text-green-600',
    rose:    'from-rose-500/15 to-rose-500/5 border-rose-500/30 text-rose-400',
    amber:   'from-[#1B2A7B]/15 to-[#1B2A7B]/5 border-[#1B2A7B]/30 text-[#1B2A7B]',
    slate:   'from-slate-500/15 to-slate-500/5 border-slate-500/30 text-slate-600',
    sky:     'from-[#1B2A7B]/10 to-[#1B2A7B]/5 border-[#1B2A7B]/40 text-[#1B2A7B]',
    red:     'from-red-500/20 to-red-500/5 border-red-300 text-red-400',
  };
  return (
    <div
      className={`rounded-xl p-3 bg-gradient-to-br border ${toneMap[tone] || toneMap.slate}`}
      data-testid={testid}
    >
      <div className="flex items-center justify-between">
        <p className="text-[11px] uppercase tracking-wider text-slate-500">{label}</p>
        <span className="opacity-80">{icon}</span>
      </div>
      <p className="text-2xl font-bold text-[#1B2A7B] mt-1">{value ?? 0}</p>
      <p className="text-[10px] text-slate-500 mt-0.5">Today</p>
    </div>
  );
};

const STATUS_PILLS = [
  { value: 'all',        label: 'All' },
  { value: 'confirmed',  label: 'Confirmed' },
  { value: 'checked_in', label: 'Checked-in' },
  { value: 'no_show',    label: 'No Show' },
  { value: 'walkin',     label: 'Walk-in' },
];

const DateDetailsPopup = ({
  date, mode, bookings, rawTotal, loading,
  search, onSearchChange, statusFilter, onStatusFilterChange,
  onClose, propertyName,
}) => {
  const modeLabel = mode === 'checkout' ? 'Check-outs' : mode === 'booking' ? 'Bookings' : 'Check-ins';
  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white border border-slate-200 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-3xl max-h-[92vh] flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="admin-date-popup"
      >
        {/* Header */}
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-wider text-[#1B2A7B]">{modeLabel} · {propertyName}</p>
            <h3 className="text-slate-900 font-semibold text-lg" data-testid="admin-popup-date-title">
              {date ? format(new Date(date), 'EEEE, MMM d, yyyy') : ''}
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {loading ? 'Loading…' : `${bookings.length} of ${rawTotal} booking${rawTotal === 1 ? '' : 's'}`}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-[#1B2A7B]"
            data-testid="admin-popup-close"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Controls */}
        <div className="px-4 py-3 border-b border-slate-200 space-y-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search by guest, booking id, phone, OTA, property, room…"
              className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-slate-900 text-sm placeholder-slate-500 focus:border-[#1B2A7B] focus:ring-1 focus:ring-[#1B2A7B] outline-none"
              data-testid="admin-popup-search"
            />
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {STATUS_PILLS.map(p => (
              <button
                key={p.value}
                onClick={() => onStatusFilterChange(p.value)}
                className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors
                  ${statusFilter === p.value
                    ? 'bg-[#1B2A7B] text-white border-[#1B2A7B]'
                    : 'bg-slate-100 text-slate-600 border-slate-200 hover:border-[#1B2A7B]/40'}
                `}
                data-testid={`admin-popup-status-${p.value}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto p-3">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 text-[#1B2A7B] animate-spin" />
            </div>
          ) : bookings.length === 0 ? (
            <div className="text-center py-16 text-slate-500">
              <CalendarIcon className="w-10 h-10 mx-auto mb-2 text-slate-700" />
              <p className="text-sm">No bookings match this view.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {bookings.map((b, i) => (
                <BookingRow key={b.booking_id || i} b={b} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const OWNER_STATUS_TONE = {
  Approved: 'bg-[#1B2A7B]/15 text-green-600 border-[#1B2A7B]/30',
  Rejected: 'bg-rose-500/15 text-rose-400 border-rose-500/30',
  Pending:  'bg-[#1B2A7B]/15 text-[#1B2A7B] border-[#1B2A7B]/30',
};

const BOOKING_STATUS_TONE = {
  cancelled:   'bg-rose-500/15 text-rose-400 border-rose-500/30',
  checked_in:  'bg-[#1B2A7B]/15 text-green-600 border-[#1B2A7B]/30',
  checked_out: 'bg-slate-500/15 text-slate-600 border-slate-500/30',
  no_show:     'bg-rose-500/15 text-rose-400 border-rose-500/30',
  confirmed:   'bg-[#1B2A7B]/10 text-[#1B2A7B] border-[#1B2A7B]/40',
};

const BookingRow = ({ b }) => {
  // iter176 — Local state for the Send Email quick modal (per-row).
  const [emailOpen, setEmailOpen] = React.useState(false);
  const status = (b.booking_status || 'confirmed').toLowerCase();
  const bookingTone = BOOKING_STATUS_TONE[status] || BOOKING_STATUS_TONE.confirmed;
  const ownerStatus = b.owner_status || 'Pending';
  const ownerTone = OWNER_STATUS_TONE[ownerStatus] || OWNER_STATUS_TONE.Pending;
  const displayCheckin  = b.check_in_date  || (b.check_in  || '').slice(0, 10) || '—';
  const displayCheckout = b.check_out_date || (b.check_out || '').slice(0, 10) || '—';
  // iter154 — CHECK-IN DONE detection uses the SAME backend field the
  // Hotel Owner app writes to (`checkin_decision`), plus the derived
  // booking_status='checked_in' for legacy rows. Green tone is applied ONLY
  // when this true — never based on the calendar date.
  const isCheckedIn =
    b.checkin_decision === 'confirmed' || status === 'checked_in';
  // Format DD/MM/YYYY for the visible date fields (backend gives ISO YYYY-MM-DD).
  const fmtDMY = (iso) => {
    if (!iso || typeof iso !== 'string' || iso.length < 10) return '—';
    const [y, m, d] = iso.slice(0, 10).split('-');
    return (y && m && d) ? `${d}/${m}/${y}` : iso;
  };
  const bookingDate = fmtDMY(b.booking_date || (b.created_at || '').slice(0, 10));
  const checkinDMY  = fmtDMY(displayCheckin);
  const checkoutDMY = fmtDMY(displayCheckout);
  // Guest breakdown: prefer adults/children split; fall back to aggregate.
  // iter184 — Guests must render exactly as stored (no parsing / no
  // reformatting / no default 0). ``rawGuestValue`` returns whatever
  // the source layer wrote and falls back to "—" only when the field
  // is truly absent.
  const guestDisplay = rawGuestValue(b);
  const roomCount = b.no_of_rooms || b.room_count || (Array.isArray(b.rooms) ? b.rooms.length : (b.rooms || 1));
  return (
    <div
      className={`border rounded-xl p-3 transition-colors ${
        isCheckedIn
          ? 'bg-[#1B2A7B]/10 border-[#1B2A7B]/50 hover:border-[#1B2A7B]'
          : 'bg-slate-100 border-slate-200 hover:border-[#1B2A7B]/40'
      }`}
      data-testid={`admin-popup-booking-${b.booking_id}`}
      data-checkin-done={isCheckedIn ? 'true' : 'false'}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-wider text-slate-500">Booking ID</p>
          <p className="text-sm font-semibold text-slate-900 truncate">{b.booking_id || '—'}</p>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap justify-end">
          {isCheckedIn && (
            <span
              className="px-2 py-0.5 rounded-full text-[10px] font-semibold border bg-[#1B2A7B]/25 text-green-700 border-[#1B2A7B]/60"
              data-testid={`checkin-done-badge-${b.booking_id}`}
            >
              CHECK-IN DONE
            </span>
          )}
          {b.is_walkin && (
            <span className="px-2 py-0.5 rounded-full text-[10px] font-medium border bg-[#1B2A7B]/10 text-[#1B2A7B] border-[#1B2A7B]/40">Walk-in</span>
          )}
          {!isCheckedIn && (
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${bookingTone}`}>
              {status.replace('_', ' ')}
            </span>
          )}
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${ownerTone}`}>
            Owner: {ownerStatus}
          </span>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
        <RowField icon={<User className="w-3.5 h-3.5" />}     label="Guest"        value={b.guest_name} />
        <RowField icon={<Phone className="w-3.5 h-3.5" />}    label="Guest Phone"  value={b.guest_phone} />
        <RowField icon={<Building2 className="w-3.5 h-3.5" />} label="Property"    value={b.property_name} />
        {/* iter286 — Admin Panel-sourced OTA property identifier (e.g. "86422612").
            Shown only when present so legacy bookings don't carry an empty row. */}
        {b.ota_property_id ? (
          <RowField
            icon={<Building2 className="w-3.5 h-3.5" />}
            label="Property ID"
            value={b.ota_property_id}
          />
        ) : null}
        {/* iter120 — Owner Name + Owner Mobile from Master Form (onboarding_forms). */}
        <RowField icon={<ShieldCheck className="w-3.5 h-3.5" />} label="Owner Name"   value={b.property_owner || '—'} />
        <RowField icon={<Phone className="w-3.5 h-3.5" />}       label="Owner Mobile" value={b.property_owner_phone || '—'} />
        {/* iter191 — OTA / Source: back to inline left-aligned row. */}
        <RowField
          icon={<Globe className="w-3.5 h-3.5" />}
          label="OTA / Source"
          value={b.ota_source ? <OtaLogo otaName={b.ota_source} /> : '—'}
          testid={`ota-source-${b.booking_id}`}
        />
        <RowField icon={<Home className="w-3.5 h-3.5" />}     label="Room Type"    value={b.room_type || '—'} />
        {/* iter120 — Booking Amount (final payable). */}
        <RowField
          icon={<IndianRupee className="w-3.5 h-3.5" />}
          label="Booking Amount"
          value={
            b.booking_amount != null && b.booking_amount !== ''
              ? `₹${Number(b.booking_amount).toLocaleString('en-IN')}`
              : '—'
          }
          strong
          testid={`booking-amount-${b.booking_id}`}
        />
        {/* iter154 — Booking Date + Guests + Rooms (BFR view). */}
        <RowField icon={<CalendarIcon className="w-3.5 h-3.5" />}    label="Booking Date" value={bookingDate}  testid={`booking-date-${b.booking_id}`} />
        <RowField icon={<User className="w-3.5 h-3.5" />}            label="Guests"       value={guestDisplay} testid={`guest-count-${b.booking_id}`} />
        <RowField icon={<Home className="w-3.5 h-3.5" />}            label="Rooms"        value={String(roomCount)}  testid={`room-count-${b.booking_id}`} />
        {/* iter183 — City sourced verbatim from the stored booking record. */}
        <RowField icon={<MapPin className="w-3.5 h-3.5" />}          label="City"         value={b.city || '—'}  testid={`city-${b.booking_id}`} />
        <RowField icon={<ArrowDownToLine className="w-3.5 h-3.5" />} label="Check-in"     value={checkinDMY} />
        <RowField icon={<ArrowUpFromLine className="w-3.5 h-3.5" />} label="Check-out"    value={checkoutDMY} />
      </div>
      {/* iter176 — Send Email opens an in-place modal with the admin-panel
          templates auto-detecting the OTA from the booking. */}
      <div className="mt-3 pt-3 border-t border-slate-200 flex justify-end">
        <button
          type="button"
          onClick={() => setEmailOpen(true)}
          className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded-lg bg-[#1B2A7B]/10 text-[#1B2A7B] border border-[#1B2A7B]/40 hover:bg-[#1B2A7B]/25"
          data-testid={`send-email-${b.booking_id}`}
        >
          <Mail className="w-3.5 h-3.5" /> Send Email
        </button>
      </div>
      {emailOpen && (
        <SendEmailModal
          booking={b}
          onClose={() => setEmailOpen(false)}
          onSent={() => setEmailOpen(false)}
        />
      )}
    </div>
  );
};

const RowField = ({ icon, label, value, strong = false, testid }) => (
  <div className="flex items-start gap-1.5 text-slate-600 min-w-0" data-testid={testid}>
    <span className="text-slate-500 mt-0.5 flex-shrink-0">{icon}</span>
    <div className="min-w-0">
      <p className="text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
      <p className={`truncate ${strong ? 'text-sm font-semibold text-[#1B2A7B]' : 'text-xs text-slate-700'}`}>
        {value || '—'}
      </p>
    </div>
  </div>
);

export default AdminCalendarScreen;
